import React, { useEffect, useState } from 'react';
import { Play, ArrowUpRight, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useI18n } from '../i18n/I18nProvider';
import { fetchSermons } from '../lib/content';
import { site } from '../data/mock';

function formatDate(iso, lang) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) { return ''; }
}

export default function Sermons() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchSermons();
        setItems(data.items || []);
      } catch (err) { console.error('Failed to load sermons', err); }
      setLoading(false);
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await fetchSermons(true);
      setItems(data.items || []);
    } catch (err) { console.error('Failed to refresh sermons', err); }
    setLoading(false);
  };

  if (!loading && items.length === 0) return null;

  const copy = lang === 'es'
    ? {
        eyebrow: 'Sermones recientes',
        title_1: 'Palabra fresca,', title_2: 'cada semana.',
        sub: 'Mira nuestras ense\u00f1anzas m\u00e1s recientes del canal Discover Grow Serve. Nuevos videos se sincronizan autom\u00e1ticamente aqu\u00ed.',
        watch: 'Ver en YouTube', channel: 'Visita el canal', refresh: 'Actualizar',
      }
    : {
        eyebrow: 'Latest Sermons',
        title_1: 'Fresh Word,', title_2: 'every week.',
        sub: 'Catch our most recent teachings from the Discover Grow Serve channel. New videos sync here automatically.',
        watch: 'Watch on YouTube', channel: 'Visit channel', refresh: 'Refresh',
      };

  return (
    <section id="sermons" className="relative py-24 lg:py-32 bg-[#f5efe3]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">{copy.eyebrow}</span>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">
              {copy.title_1} <span className="italic">{copy.title_2}</span>
            </h2>
            <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed">{copy.sub}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={refresh} variant="outline" className="rounded-full h-10 border-[#1f3a2a]/20 text-[#1f3a2a] hover:bg-[#1f3a2a]/5">
              <RefreshCw size={15} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> {copy.refresh}
            </Button>
            <Button asChild className="bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] rounded-full h-10 px-5">
              <a href={site.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">{copy.channel} <ArrowUpRight size={15} /></a>
            </Button>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 9).map((v) => (
            <article key={v.id} className="group bg-white border border-[#e5dcc5] rounded-2xl overflow-hidden card-rise">
              <div className="relative aspect-video bg-[#1f3a2a]">
                {playing === v.id ? (
                  <iframe
                    title={v.title}
                    src={`${v.embed_url}?autoplay=1&rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                ) : (
                  <button
                    onClick={() => setPlaying(v.id)}
                    aria-label={`Play ${v.title}`}
                    className="absolute inset-0 w-full h-full group/btn"
                  >
                    <img src={v.thumbnail} alt="" className="w-full h-full object-cover group-hover/btn:scale-105 transition-transform duration-500" loading="lazy" />
                    <span className="absolute inset-0 bg-[#1f3a2a]/35 group-hover/btn:bg-[#1f3a2a]/50 transition-colors flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full bg-[#d4a63d] text-[#1f3a2a] flex items-center justify-center shadow-xl group-hover/btn:scale-110 transition-transform">
                        <Play size={26} fill="currentColor" />
                      </span>
                    </span>
                  </button>
                )}
              </div>
              <div className="p-5">
                <p className="text-[11px] uppercase tracking-wider-xtra text-[#8a7a4e]">{formatDate(v.published, lang)}</p>
                <h3 className="mt-1.5 font-serif-display text-xl text-[#1f3a2a] leading-tight line-clamp-2">{v.title}</h3>
                <div className="mt-3 flex items-center justify-between">
                  <a href={v.url} target="_blank" rel="noreferrer" className="text-sm text-[#2e5340] hover:text-[#1f3a2a] link-underline inline-flex items-center gap-1">
                    {copy.watch} <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
